import { useTranslation } from "react-i18next";
import type { MatchingV2Speaker } from "@/api/types";
import { cn } from "@/lib/utils";

/**
 * §7.2 of `docs/api/matching-v2.contract.md` — «Speaker bar» row above
 * the transcript. Backend pre-sorts speakers by `duration_ratio` desc
 * (contract §4) so we render in iteration order: no client-side scoring.
 *
 * Each row carries:
 *   • employee_full_name (or speaker label as a fallback when the
 *     binding is missing — useful during partial matching);
 *   • optional ЛПР badge — surfaced only when role_hint resolves to
 *     `decision_maker` AND confidence ≥ 0.7 (contract §9.1, frozen);
 *   • duration_ratio formatted as a percent + total_words.
 *
 * Click → scroll to the first utterance of this speaker. The parent
 * (TranscriptTab v2) supplies the lookup table; we just forward the
 * label so callers can pin their own focus strategy.
 */
export interface SpeakerSummaryBarProps {
  speakers: MatchingV2Speaker[];
  /**
   * Called with the speaker label when the operator clicks a row.
   * No-op by default keeps tests trivial and lets the parent decide
   * the scroll/focus behaviour without coupling to a specific impl.
   */
  onSpeakerClick?: (label: string) => void;
}

/** Decision-maker badge threshold — frozen at 0.7 per contract §9.1. */
const DECISION_MAKER_CONFIDENCE_THRESHOLD = 0.7;

export function SpeakerSummaryBar({
  speakers,
  onSpeakerClick,
}: SpeakerSummaryBarProps) {
  const { t } = useTranslation();
  // Server already filters `is_noise=true` rows out, but guard
  // belt-and-braces in case the FE ever flips `?include_noise=true`.
  const visible = speakers.filter((s) => !s.is_noise);
  if (visible.length === 0) return null;
  return (
    <section
      className="rounded border border-border bg-card p-3 text-sm"
      aria-label={t("meetings.card.matchingV2.summaryBar.title")}
      data-testid="speaker-summary-bar"
    >
      <header className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t("meetings.card.matchingV2.summaryBar.title")}
      </header>
      <ul className="space-y-1">
        {visible.map((sp) => (
          <SpeakerRow key={sp.label} speaker={sp} onClick={onSpeakerClick} />
        ))}
      </ul>
    </section>
  );
}

interface SpeakerRowProps {
  speaker: MatchingV2Speaker;
  onClick?: (label: string) => void;
}

function SpeakerRow({ speaker, onClick }: SpeakerRowProps) {
  const { t } = useTranslation();
  const displayName =
    speaker.binding?.employee_full_name ??
    t("meetings.card.matchingV2.summaryBar.unknownName");
  const percent = Math.round(speaker.duration_ratio * 100);
  const isDecisionMaker =
    speaker.role_hint?.value === "decision_maker" &&
    speaker.role_hint.confidence >= DECISION_MAKER_CONFIDENCE_THRESHOLD;
  const handleClick = () => onClick?.(speaker.label);
  return (
    <li>
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          "flex w-full items-center gap-2 rounded px-2 py-1 text-left",
          "transition-colors hover:bg-muted focus-visible:outline-none",
          "focus-visible:ring-1 focus-visible:ring-ring",
        )}
        aria-label={t("meetings.card.matchingV2.summaryBar.rowAria", {
          name: displayName,
          percent,
          words: speaker.total_words,
        })}
        data-testid="speaker-summary-row"
        data-speaker-label={speaker.label}
      >
        <span className="flex-1 truncate font-medium">{displayName}</span>
        {isDecisionMaker && (
          <span
            className="rounded bg-yellow-100 px-1.5 py-0.5 text-xs font-semibold text-yellow-900 dark:bg-yellow-900/40 dark:text-yellow-100"
            aria-label={t(
              "meetings.card.matchingV2.summaryBar.decisionMakerAria",
            )}
            data-testid="speaker-summary-dm-badge"
          >
            {t("meetings.card.matchingV2.summaryBar.decisionMakerBadge")}
          </span>
        )}
        <span
          className="font-mono text-xs tabular-nums text-muted-foreground"
          data-testid="speaker-summary-ratio"
        >
          {percent}%
        </span>
        <span
          className="font-mono text-xs tabular-nums text-muted-foreground"
          data-testid="speaker-summary-words"
        >
          {speaker.total_words}{" "}
          {t("meetings.card.matchingV2.summaryBar.wordsSuffix")}
        </span>
      </button>
    </li>
  );
}
